import { eventBus, EVENTS } from './EventBus'

export class ColumnManager {
  constructor() {
    this.columns = new Map()
    this.widths = new Map()
    this.order = []
    this.resizing = null
    this.BLACK_CIRCLE_CODE = '3170'
    this.displayLabels = new Map()

    this.defaultWidths = {
      no: 40,
      shiperName: 60,
      hinmeiCode: 40,
      cargoMark: 56,
      shippingDate: 50,
      receivedDate: 50,
      grade: 40,
      class: 40,
      cargoForm: 40,
      quantityUnit: 40,
      packageQuantity: 40,
      boxCount: 44,
      quantity: 44,
      remain: 44,
      listing_quantity: 50,
      unitPrice: 60,
      purchasePrice: 50,
      benefit: 50,
      branch: 32
    }

    this.initializeDefaultColumns()
  }

  static buildColumnKey(customerCode, branchCode, index) {
    return `${customerCode}::${String(branchCode ?? '')}::${index}`
  }

  static parseColumnKey(columnKey) {
    const str = String(columnKey ?? '')
    const parts = str.split('::')
    if (parts.length === 3) {
      return {
        customerCode: parts[0],
        branchCode: parts[1],
        index: Number(parts[2])
      }
    }
    return { customerCode: str, branchCode: '', index: 0 }
  }

  static isCompositeKey(columnKey) {
    return String(columnKey ?? '').includes('::')
  }

  initializeDefaultColumns() {
    const baseColumns = [
      { id: 'no', label: 'No.', type: 'row-number', fixed: true },
      { id: 'shiperName', label: '出荷者', type: 'data', fixed: true },
      { id: 'hinmeiCode', label: '品目', type: 'data', fixed: true },
      { id: 'cargoMark', label: '荷印', type: 'data', fixed: true },
      { id: 'shippingDate', label: '出荷\n日時', type: 'data', fixed: true },
      { id: 'receivedDate', label: '送受信\n日時', type: 'data', fixed: true },
      { id: 'grade', label: '等級', type: 'data', fixed: true },
      { id: 'class', label: '階級', type: 'data', fixed: true },
      { id: 'cargoForm', label: '荷姿', type: 'data', fixed: true },
      { id: 'quantityUnit', label: '量目', type: 'data', fixed: true },
      { id: 'packageQuantity', label: '入数', type: 'data', fixed: true },
      { id: 'boxCount', label: '箱数', type: 'data', fixed: true },
      { id: 'quantity', label: '数量', type: 'data', fixed: true },
      { id: 'remain', label: '残数', type: 'data', fixed: true },
      { id: 'listing_quantity', label: '上場', type: 'data', fixed: true },
      { id: 'unitPrice', label: '仕入れ \n 価格', type: 'data', fixed: true },
      { id: 'purchasePrice', label: '単価', type: 'data', fixed: true },
      { id: 'benefit', label: '利益', type: 'data', fixed: true },
      { id: 'branch', label: '買\n受\n人', type: 'data', fixed: true }
    ]

    baseColumns.forEach((col) => {
      this.columns.set(col.id, col)
      this.widths.set(col.id, this.defaultWidths[col.id] || 80)
      this.order.push(col.id)
    })
  }

  hasCustomerCode(columnKey) {
    for (const [, col] of this.columns.entries()) {
      if (
        (col.type === 'customer' || col.type === 'black-circle') &&
        String(col.customerCode) === String(columnKey)
      ) {
        return true
      }
    }
    return false
  }

  getColumnByCustomerCode(columnKey) {
    for (const [, col] of this.columns.entries()) {
      if (
        (col.type === 'customer' || col.type === 'black-circle') &&
        String(col.customerCode) === String(columnKey)
      ) {
        return col
      }
    }
    return null
  }

  addCustomerColumn(columnKey, label) {
    const id = `customer_${columnKey}`
    if (this.columns.has(id)) return false
    if (this.hasCustomerCode(columnKey)) return false

    const { customerCode: defaultLabel } =
      ColumnManager.parseColumnKey(columnKey)
    this.columns.set(id, {
      id,
      customerCode: columnKey,
      label: label || defaultLabel,
      type: 'customer',
      fixed: false,
      editable: true
    })
    this.widths.set(id, 80)
    this.order.push(id)

    const { customerCode, branchCode, index } =
      ColumnManager.parseColumnKey(columnKey)
    if (!this.customerHeaderFields) this.customerHeaderFields = new Map()
    if (!this.customerHeaderFields.has(columnKey)) {
      this.customerHeaderFields.set(columnKey, {
        torioiki: '',
        basho: '',
        edaban: branchCode,
        _originalBranch: branchCode,
        _originalIndex: index,
        _customerCode: customerCode
      })
    }

    eventBus.emit(EVENTS.COLUMN_ADDED, {
      id,
      customerCode: columnKey,
      type: 'customer'
    })
    return true
  }

  insertCustomerColumnAfter(afterColumnKey, newColumnKey, label) {
    const newId = `customer_${newColumnKey}`
    if (this.columns.has(newId)) return false
    if (this.hasCustomerCode(newColumnKey)) return false

    let afterId = null
    for (const [id, col] of this.columns.entries()) {
      if (
        (col.type === 'customer' || col.type === 'black-circle') &&
        col.customerCode === afterColumnKey
      ) {
        afterId = id
        break
      }
    }

    if (!afterId) return this.addCustomerColumn(newColumnKey, label)

    const { customerCode: defaultLabel } =
      ColumnManager.parseColumnKey(newColumnKey)
    this.columns.set(newId, {
      id: newId,
      customerCode: newColumnKey,
      label: label || defaultLabel,
      type: 'customer',
      fixed: false,
      editable: true
    })
    this.widths.set(newId, 80)

    const afterIndex = this.order.indexOf(afterId)
    this.order.splice(afterIndex + 1, 0, newId)

    eventBus.emit(EVENTS.COLUMN_ADDED, {
      id: newId,
      customerCode: newColumnKey,
      type: 'customer',
      insertAfter: afterColumnKey
    })
    return true
  }

  insertCustomerColumnBefore(beforeColumnKey, newColumnKey, label) {
    const newId = `customer_${newColumnKey}`
    if (this.columns.has(newId)) return false
    if (this.hasCustomerCode(newColumnKey)) return false

    let beforeId = null
    for (const [id, col] of this.columns.entries()) {
      if (
        (col.type === 'customer' || col.type === 'black-circle') &&
        col.customerCode === beforeColumnKey
      ) {
        beforeId = id
        break
      }
    }

    if (!beforeId) return this.addCustomerColumn(newColumnKey, label)

    const { customerCode: defaultLabel } =
      ColumnManager.parseColumnKey(newColumnKey)
    this.columns.set(newId, {
      id: newId,
      customerCode: newColumnKey,
      label: label || defaultLabel,
      type: 'customer',
      fixed: false,
      editable: true
    })
    this.widths.set(newId, 80)

    const beforeIndex = this.order.indexOf(beforeId)
    this.order.splice(beforeIndex, 0, newId)

    eventBus.emit(EVENTS.COLUMN_ADDED, {
      id: newId,
      customerCode: newColumnKey,
      type: 'customer',
      insertBefore: beforeColumnKey
    })
    return true
  }

  addBlackCircleColumn() {
    const id = `black_${this.BLACK_CIRCLE_CODE}`
    if (this.columns.has(id)) return false

    if (this.hasCustomerCode(this.BLACK_CIRCLE_CODE)) {
      const existingColumn = this.getColumnByCustomerCode(
        this.BLACK_CIRCLE_CODE
      )
      if (existingColumn && existingColumn.type === 'customer') {
        existingColumn.type = 'black-circle'
        existingColumn.special = true
        return true
      }
      return false
    }

    this.columns.set(id, {
      id,
      customerCode: this.BLACK_CIRCLE_CODE,
      label: this.BLACK_CIRCLE_CODE,
      type: 'black-circle',
      fixed: false,
      editable: true,
      special: true
    })
    this.widths.set(id, 80)
    this.order.push(id)

    eventBus.emit(EVENTS.COLUMN_ADDED, {
      id,
      customerCode: this.BLACK_CIRCLE_CODE,
      type: 'black-circle'
    })
    return true
  }

  removeColumn(id) {
    const col = this.columns.get(id)
    if (!col || col.fixed) return false

    const columnKey = col.customerCode

    this.columns.delete(id)
    this.widths.delete(id)
    this.order = this.order.filter((colId) => colId !== id)

    if (this.customerHeaderFields) {
      this.customerHeaderFields.delete(columnKey)
    }
    if (this.displayLabels) {
      this.displayLabels.delete(columnKey)
    }

    eventBus.emit(EVENTS.COLUMN_REMOVED, { id, column: col })
    return true
  }

  removeCustomerColumn(customerCode) {
    const colId = `customer_${customerCode}`
    const col = this.columns.get(colId)
    if (!col || col.fixed) return null

    const snapshot = {
      colId,
      col: { ...col },
      width: this.widths.get(colId) ?? 80,
      orderSnapshot: [...this.order],
      headerFields: this.customerHeaderFields?.get(customerCode)
        ? { ...this.customerHeaderFields.get(customerCode) }
        : null
    }

    this.columns.delete(colId)
    this.widths.delete(colId)
    this.order = this.order.filter((id) => id !== colId)
    this.customerHeaderFields?.delete(customerCode)

    eventBus.emit(EVENTS.COLUMN_REMOVED, { id: colId, column: col })

    return snapshot
  }

  restoreCustomerColumn(snapshot) {
    if (!snapshot) return false
    const { colId, col, width, orderSnapshot, headerFields } = snapshot

    this.columns.set(colId, { ...col })
    this.widths.set(colId, width)
    this.order = [...orderSnapshot]

    if (headerFields) {
      if (!this.customerHeaderFields) this.customerHeaderFields = new Map()
      this.customerHeaderFields.set(col.customerCode, { ...headerFields })
    }

    eventBus.emit(EVENTS.COLUMN_ADDED, {
      id: colId,
      customerCode: col.customerCode,
      type: 'restore'
    })
    return true
  }

  bulkAddCustomerColumns(columnDefs) {
    let added = 0
    columnDefs.forEach((def) => {
      const key = typeof def === 'string' ? def : def.columnKey
      const label = typeof def === 'string' ? def : def.label || key
      if (this.addCustomerColumn(key, label)) added++
    })
    return added
  }

  getWidth(id) {
    return this.widths.get(id) || 80
  }

  setWidth(id, width) {
    const validWidth = Math.max(50, Math.min(500, width))
    this.widths.set(id, validWidth)
    eventBus.emit(EVENTS.COLUMN_RESIZED, { id, width: validWidth })
  }

  startResize(id, startX, startWidth) {
    this.resizing = { id, startX, startWidth }
  }

  updateResize(currentX) {
    if (!this.resizing) return null
    const diff = currentX - this.resizing.startX
    const newWidth = Math.max(50, this.resizing.startWidth + diff)
    return { id: this.resizing.id, width: newWidth }
  }

  endResize() {
    this.resizing = null
  }

  getColumns() {
    return this.order.map((id) => ({
      ...this.columns.get(id),
      width: this.widths.get(id)
    }))
  }

  getCustomerColumns() {
    return this.order
      .filter((id) => {
        const col = this.columns.get(id)
        return col && (col.type === 'customer' || col.type === 'black-circle')
      })
      .map((id) => ({ ...this.columns.get(id), width: this.widths.get(id) }))
  }

  getBaseColumns() {
    return this.order
      .filter((id) => {
        const col = this.columns.get(id)
        return col && col.type !== 'customer' && col.type !== 'black-circle'
      })
      .map((id) => ({ ...this.columns.get(id), width: this.widths.get(id) }))
  }

  getCustomerCodes() {
    return this.order
      .map((id) => this.columns.get(id))
      .filter(
        (col) => col && (col.type === 'customer' || col.type === 'black-circle')
      )
      .map((col) => col.customerCode)
  }

  getColumnCount() {
    return this.columns.size
  }

  hasBlackCircleColumn() {
    return this.columns.has(`black_${this.BLACK_CIRCLE_CODE}`)
  }

  reorderColumns(newOrder, options = {}) {
    if (newOrder.length !== this.order.length) return false
    if (!newOrder.every((id) => this.columns.has(id))) return false

    const oldConfig = this.exportConfig()
    this.order = [...newOrder]

    if (options?.emitEvent !== false) {
      eventBus.emit(EVENTS.COLUMN_REORDERED, { order: this.order, oldConfig })
    }
    return true
  }

  resetWidths() {
    this.widths.clear()
    this.order.forEach((id) => {
      this.widths.set(id, this.defaultWidths[id] || 80)
    })
    eventBus.emit(EVENTS.COLUMN_RESIZED, { reset: true })
  }

  exportConfig() {
    return {
      columns: JSON.parse(JSON.stringify(Array.from(this.columns.entries()))),
      widths: JSON.parse(JSON.stringify(Array.from(this.widths.entries()))),
      order: JSON.parse(JSON.stringify([...this.order])),
      customerHeaderFields: this.customerHeaderFields
        ? JSON.parse(
            JSON.stringify(Array.from(this.customerHeaderFields.entries()))
          )
        : []
    }
  }

  importConfig(config) {
    if (!config) return false
    try {
      if (config.columns)
        this.columns = new Map(JSON.parse(JSON.stringify(config.columns)))
      if (config.widths)
        this.widths = new Map(JSON.parse(JSON.stringify(config.widths)))
      if (config.order) this.order = JSON.parse(JSON.stringify(config.order))
      if (config.customerHeaderFields)
        this.customerHeaderFields = new Map(
          JSON.parse(JSON.stringify(config.customerHeaderFields))
        )

      const compactDateColumns = [
        { id: 'shippingDate', label: '出荷\n日時' },
        { id: 'receivedDate', label: '送受信\n日時' }
      ]
      let previousId = 'cargoMark'
      compactDateColumns.forEach(({ id, label }) => {
        const column = this.columns.get(id) || {
          id,
          type: 'data',
          fixed: true
        }
        column.label = label
        this.columns.set(id, column)
        this.widths.set(id, 50)

        this.order = this.order.filter((columnId) => columnId !== id)
        const anchorIndex = this.order.indexOf(previousId)
        const fallbackIndex = this.order.indexOf('hinmeiCode')
        const insertIndex =
          anchorIndex >= 0
            ? anchorIndex + 1
            : fallbackIndex >= 0
              ? fallbackIndex + 1
              : this.order.length
        this.order.splice(insertIndex, 0, id)
        previousId = id
      })

      eventBus.emit(EVENTS.COLUMN_REORDERED, {
        order: this.order,
        isImport: true
      })
      return true
    } catch (error) {
      console.error('Failed to import column config:', error)
      return false
    }
  }

  setCustomerHeaderField(columnKey, fieldName, value) {
    if (!this.customerHeaderFields) this.customerHeaderFields = new Map()

    if (!this.customerHeaderFields.has(columnKey)) {
      const { branchCode, index } = ColumnManager.parseColumnKey(columnKey)
      this.customerHeaderFields.set(columnKey, {
        torioiki: '',
        basho: '',
        edaban: '',
        _originalBranch: branchCode,
        _originalIndex: index,
        _customerCode: ColumnManager.parseColumnKey(columnKey).customerCode
      })
    }

    const fields = this.customerHeaderFields.get(columnKey)
    const oldValue = fields[fieldName]
    fields[fieldName] = value

    const eventMap = {
      torioiki: EVENTS.CUSTOMER_TORIOIKI_CHANGED,
      basho: EVENTS.CUSTOMER_BASHO_CHANGED,
      edaban: EVENTS.CUSTOMER_EDABAN_CHANGED
    }

    eventBus.emit(eventMap[fieldName], {
      customerCode: columnKey,
      fieldName,
      oldValue,
      newValue: value
    })
    eventBus.emit(EVENTS.CUSTOMER_HEADER_CHANGED, {
      customerCode: columnKey,
      fieldName,
      oldValue,
      newValue: value,
      allFields: { ...fields }
    })
    return true
  }

  updateCustomerCode(oldKey, newKey) {
    let foundColumn = null
    let foundId = null

    for (const [id, col] of this.columns.entries()) {
      if (
        (col.type === 'customer' || col.type === 'black-circle') &&
        col.customerCode === oldKey
      ) {
        foundColumn = col
        foundId = id
        break
      }
    }

    if (!foundColumn) return false

    foundColumn.customerCode = newKey
    const { customerCode } = ColumnManager.parseColumnKey(newKey)
    const { customerCode: oldCustomerCode } = ColumnManager.parseColumnKey(oldKey)
    
    if (customerCode !== oldCustomerCode || foundColumn.label !== '') {
      foundColumn.label = customerCode
    }

    const newId = `customer_${newKey}`

    if (foundId !== newId && foundId.startsWith('customer_')) {
      const width = this.widths.get(foundId)
      this.columns.delete(foundId)
      this.widths.delete(foundId)
      foundColumn.id = newId
      this.columns.set(newId, foundColumn)
      this.widths.set(newId, width || 80)

      const orderIndex = this.order.indexOf(foundId)
      if (orderIndex !== -1) this.order[orderIndex] = newId
    }

    if (!this.customerHeaderFields) this.customerHeaderFields = new Map()

    const oldFields = this.customerHeaderFields.get(oldKey)
    const { customerCode: newCustomerCode, branchCode: newBranchCode } =
      ColumnManager.parseColumnKey(newKey)

    if (oldFields) {
      this.customerHeaderFields.delete(oldKey)
      this.customerHeaderFields.set(newKey, {
        ...oldFields,
        // Temporary NEW_* columns are created before the real customer code is
        // known. Once renamed, their source identity must become the new
        // composite key; otherwise later branch edits can target the first
        // existing blank-branch column and move its quantities into this column.
        _originalBranch: oldKey.startsWith('NEW_')
          ? newBranchCode
          : oldFields._originalBranch,
        _originalIndex: oldKey.startsWith('NEW_')
          ? ColumnManager.parseColumnKey(newKey).index
          : oldFields._originalIndex,
        _customerCode: newCustomerCode
      })
    } else {
      this.customerHeaderFields.set(newKey, {
        torioiki: '',
        basho: '',
        edaban: newBranchCode,
        _originalBranch: newBranchCode,
        _originalIndex: ColumnManager.parseColumnKey(newKey).index,
        _customerCode: newCustomerCode
      })
    }

    eventBus.emit(EVENTS.CUSTOMER_HEADER_CHANGED, {
      oldCode: oldKey,
      newCode: newKey,
      oldId: foundId,
      newId
    })
    return true
  }

  hasDuplicateCustomerBranch(customerCode, branchCode, excludeColumnKey) {
    const targetCode = String(customerCode ?? '')
    const targetBranch = String(branchCode ?? '')

    for (const col of this.getCustomerColumns()) {
      if (col.customerCode === excludeColumnKey) continue

      const { customerCode: existingCode, branchCode: existingBranch } =
        ColumnManager.parseColumnKey(col.customerCode)

      const effectiveBranch =
        this.getCustomerHeaderField(col.customerCode, 'edaban') ||
        existingBranch

      if (
        String(existingCode ?? '') === targetCode &&
        String(effectiveBranch ?? '') === targetBranch
      ) {
        return true
      }
    }
    return false
  }

  getDuplicateCustomerDestination(columns = this.getCustomerColumns()) {
    const seen = new Map()
    const reserveCodes = { '': '0', '●': '1', '+1日': '2', '+2日': '3' }

    for (const [index, column] of columns.entries()) {
      const columnKey = column.customerCode
      const { customerCode, branchCode } = ColumnManager.parseColumnKey(columnKey)
      const fields = this.getCustomerHeaderFields(columnKey)
      const buyer = String(customerCode ?? '').trim()
      const normalizedBuyer = /^\d{1,4}$/.test(buyer)
        ? buyer.padStart(4, '0')
        : buyer
      const reserve = String(fields.torioiki ?? '').normalize('NFKC').trim()
      const destination = JSON.stringify([
        reserveCodes[reserve] ?? reserve,
        String(fields.basho ?? '').trim(),
        String(fields.edaban ?? branchCode ?? '').trim().toUpperCase(),
        normalizedBuyer
      ])

      if (seen.has(destination)) {
        return { firstColumn: seen.get(destination), secondColumn: index + 1 }
      }
      seen.set(destination, index + 1)
    }
    return null
  }

  getCustomerHeaderField(columnKey, fieldName) {
    if (!this.customerHeaderFields) this.customerHeaderFields = new Map()
    const fields = this.customerHeaderFields.get(columnKey)
    return fields ? fields[fieldName] || '' : ''
  }

  getCustomerHeaderFields(columnKey) {
    if (!this.customerHeaderFields) this.customerHeaderFields = new Map()
    return (
      this.customerHeaderFields.get(columnKey) || {
        torioiki: '',
        basho: '',
        edaban: ''
      }
    )
  }

  clearCustomerHeaderFields(columnKey) {
    this.customerHeaderFields?.delete(columnKey)
  }

  clearAllCustomerHeaderFields() {
    this.customerHeaderFields?.clear()
  }
}

export default ColumnManager
